import { Inject, Injectable } from "@angular/core";
import { Storage } from "@ionic/storage";
import * as crypto from "crypto-js";
import { Events } from "ionic-angular";
import { CacheService } from "ionic-cache";
import { CacheStorageService } from "ionic-cache/dist/cache-storage";
import { CheckUtils, Guid, logger, OdConfig } from "..";
import { StorageData } from "./storage-data";
import { StorageType } from "./storage-type";
var EncryptStorage = /** @class */ (function () {
    function EncryptStorage(config, events) {
        this.config = config;
        this.events = events;
    }
    EncryptStorage.prototype.createLocalDb = function (uri) {
        var _this = this;
        if (CheckUtils.isEmpty(uri) || uri === this._uri)
            return;
        this._uri = uri;
        this._storage = new Storage({
            name: this.config.dbName + "_" + uri,
            storeName: "_" + uri,
            driverOrder: ["indexeddb", "sqlite", "websql", "localstorage"]
        }, 0);
        this._cacheStorageService = new CacheStorageService(this._storage, this.config.cacheName);
        this._cache = new CacheService(this._cacheStorageService);
        this.load("secret-passphrase-" + this._uri, false, StorageType.SESSION).then(function (res) {
            _this._cache.clearExpired()
                .then(function () {
                if (CheckUtils.isEmpty(res)) {
                    res = Guid.guid();
                }
                _this.secretPassphraseLoaded(res);
            })
                .catch(function (err) {
                logger.info(err);
                if (CheckUtils.isEmpty(res)) {
                    res = Guid.guid();
                }
                _this.secretPassphraseLoaded(res);
            });
        });
    };
    EncryptStorage.prototype.secretPassphraseLoaded = function (value) {
        if (!CheckUtils.isEmpty(value))
            this.setSecretPassphrase(value);
        this.events.publish(EncryptStorageEvent.LOCAL_STORAGE_CREATED);
    };
    Object.defineProperty(EncryptStorage.prototype, "secretPassphrase", {
        get: function () {
            return this._secretPassphrase;
        },
        enumerable: true,
        configurable: true
    });
    EncryptStorage.prototype.setSecretPassphrase = function (value, preventEvent) {
        if (this._secretPassphrase != value) {
            this._secretPassphrase = value;
            var storageData = new StorageData();
            storageData.storageType = StorageType.SESSION;
            storageData.item = value;
            storageData.key = "secret-passphrase-" + this._uri;
            storageData.encrypt = false;
            this.saveItem(storageData);
            if (!preventEvent)
                this.events.publish(EncryptStorageEvent.SECRET_PASSPHRASE_CHANGED, !CheckUtils.isEmpty(this._secretPassphrase));
        }
    };
    EncryptStorage.prototype.deleteSecretPassphrase = function () {
        var storageData = new StorageData();
        storageData.storageType = StorageType.SESSION;
        storageData.item = null;
        storageData.key = "secret-passphrase-" + this._uri;
        storageData.encrypt = false;
        this.delete(storageData);
    };
    EncryptStorage.prototype.load = function (key, encrypt, storageType) {
        var _this = this;
        if (encrypt === void 0) { encrypt = true; }
        if (storageType === void 0) { storageType = StorageType.CACHE; }
        return new Promise(function (resolve, reject) {
            if (CheckUtils.isEmpty(_this._secretPassphrase) && _this.config.cacheEncrypted && encrypt) {
                logger.error("Error on load data in storage. SecretPassphrase is not defined.");
                reject(null);
            }
            switch (storageType) {
                case StorageType.SESSION:
                    resolve(JSON.parse(window.sessionStorage.getItem(key)));
                    break;
                case StorageType.LOCAL:
                    resolve(JSON.parse(window.localStorage.getItem(key)));
                    break;
                case StorageType.COOKIE:
                    resolve(EncryptStorage.getCookie(key));
                    break;
                case StorageType.CACHE:
                    if (CheckUtils.isEmpty(_this._cache)) {
                        logger.error("Error on load data in storage. CacheService is not defined.");
                    }
                    else {
                        _this._cache.itemExists(key).then(function (isExist) {
                            if (isExist) {
                                _this._cache.getItem(key).then(function (value) {
                                    var result = value;
                                    if (_this.config.cacheEncrypted && encrypt) {
                                        try {
                                            var bytes = crypto.AES.decrypt(value, _this._secretPassphrase);
                                            result = JSON.parse(bytes.toString(crypto.enc.Utf8));
                                        }
                                        catch (err) {
                                            resolve(null);
                                        }
                                    }
                                    resolve(result);
                                }).catch(function (err) { return logger.info(err); });
                            }
                            else {
                                resolve(null);
                            }
                        }).catch(function (err) { return logger.info(err); });
                    }
                    break;
                default:
                    reject("Error on load data in storage. StorageType is not defined.");
            }
        });
    };
    EncryptStorage.prototype.saveItem = function (storageData) {
        var result = storageData.item;
        if (result instanceof Map) {
            result = Array.from(result.entries());
        }
        if (storageData.encrypt && CheckUtils.isEmpty(this._secretPassphrase) && this.config.cacheEncrypted) {
            logger.info("can't save data in storage. SecretPassphrase is not defined.");
            return;
        }
        else if (this.config.cacheEncrypted && storageData.encrypt) {
            result = crypto.AES.encrypt(JSON.stringify(result), this._secretPassphrase).toString();
        }
        switch (storageData.storageType) {
            case StorageType.SESSION:
                window.sessionStorage.setItem(storageData.key, JSON.stringify(result));
                break;
            case StorageType.LOCAL:
                window.localStorage.setItem(storageData.key, JSON.stringify(result));
                break;
            case StorageType.COOKIE:
                document.cookie = storageData.key + "=" + String(result) + ";expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/";
                break;
            case StorageType.CACHE:
                if (CheckUtils.isEmpty(this._cache)) {
                    logger.info("Error on save data in storage. CacheService is not defined.");
                }
                else {
                    this._cache.saveItem(storageData.key, result).catch(function (err) { return logger.info(err); });
                }
                break;
            default:
                logger.info("Error on save data in storage. StorageType is not defined.");
        }
    };
    EncryptStorage.prototype.removeItem = function (storageData) {
        switch (storageData.storageType) {
            case StorageType.SESSION:
                window.sessionStorage.removeItem(storageData.key);
                break;
            case StorageType.LOCAL:
                window.localStorage.removeItem(storageData.key);
                break;
            case StorageType.COOKIE:
                document.cookie = storageData.key + "=; expires=Thu, 01-Jan-1970 00:00:01 GMT; path=/";
                break;
            case StorageType.CACHE:
                if (CheckUtils.isEmpty(this._cache)) {
                    logger.info("Error on remove data in storage. CacheService is not defined.");
                }
                else {
                    this._cache.removeItem(storageData.key).catch(function (err) { return logger.info(err); });
                }
                break;
            default:
                logger.info("Error on remove data in storage. StorageType is not defined.");
        }
    };
    EncryptStorage.prototype.saveAs = function (storageData) {
        if (!storageData.preventSave) {
            if (CheckUtils.isEmpty(storageData.item)) {
                this.removeItem(storageData);
            }
            else {
                this.saveItem(storageData);
            }
        }
        if (!storageData.preventDefault && !CheckUtils.isEmpty(storageData.event)) {
            if (storageData.value != null)
                this.events.publish(storageData.event, storageData.value);
            else
                this.events.publish(storageData.event);
        }
        return true;
    };
    EncryptStorage.prototype.delete = function (storageData) {
        if (CheckUtils.isEmpty(storageData.event))
            return false;
        this.removeItem(storageData);
        if (!storageData.preventDefault) {
            if (storageData.value != null)
                this.events.publish(storageData.event, storageData.value);
            else
                this.events.publish(storageData.event);
        }
        return true;
    };
    EncryptStorage.getCookie = function (cname) {
        var name = cname + "=";
        var decodedCookie = decodeURIComponent(document.cookie);
        var ca = decodedCookie.split(";");
        for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            while (c.charAt(0) == " ") {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }
        return "";
    };
    EncryptStorage.decorators = [
        { type: Injectable },
    ];
    /** @nocollapse */
    EncryptStorage.ctorParameters = function () { return [
        { type: OdConfig, decorators: [{ type: Inject, args: ["config",] },] },
        { type: Events, },
    ]; };
    return EncryptStorage;
}());
export { EncryptStorage };
export var EncryptStorageEvent;
(function (EncryptStorageEvent) {
    EncryptStorageEvent["LOCAL_STORAGE_CREATED"] = "encrypt-storage-local-storage:created";
    EncryptStorageEvent["SECRET_PASSPHRASE_CHANGED"] = "encrypt-storage-secret-passphrase:changed";
})(EncryptStorageEvent || (EncryptStorageEvent = {}));
//# sourceMappingURL=encrypt-storage.js.map


//////////////////
// WEBPACK FOOTER
// ../od-ts/dist/storage/encrypt-storage.js
// module id = 358
// module chunks = 14